import "./RequirementGatheringComponent.scss";
import CustomInputComponent from "@/components/ui/custom-input/CustomInputComponent.tsx";
import { CommonService } from "@/services";
import { Misc, ENV } from "@/constants";
import JoyAvatar from "@/components/common-components/joy-avatar/joy-avatar.tsx";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import commonService from "@/services/common.service.ts";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ChatLoaderComponent from "@/components/common-components/chat-loader/ChatLoaderComponent.tsx";
import TextRenderAnimationComponent
    from "@/components/animations/text-render-animation/TextRenderAnimationComponent.tsx";
import UserAvatarComponent from "@/components/common-components/user-avatar/UserAvatarComponent.tsx";
import { PROJECT_REACT_ROUTE } from "@/constants/RoutesConfig.tsx";
import { Dialog, DialogContent } from "@/components/ui/dialog.tsx";
import FileUploadComponent from "@/components/ui/file-upload/FileUploadComponent.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDispatch } from "react-redux";
import { setConversationStep, setCurrentPmSessionId, setPmConversationSuccessful, setConversationSummaryPrompt } from "@/store/actions/projectdata.action.ts";
import { setCurrentPrompt } from "@/store/actions/auth.action.ts";
import { useSelector } from "react-redux";
import { IRootReducerState } from "@/store/reducers";
import VoiceStatusIndicatorComponent from "@/components/common-components/voice-status-indicator/VoiceStatusIndicatorComponent.tsx";
import ChatInitializationLoaderComponent from "@/components/common-components/chat-initialization-loader/ChatInitializationLoaderComponent.tsx";
import RequirementGatheringWelcomeComponent from "@/components/common-components/requirement-gathering-welcome/RequirementGatheringWelcomeComponent.tsx";
import ModeSelectionComponent from "@/components/common-components/mode-selection/ModeSelectionComponent.tsx";
import VolumeControlComponent from "@/components/common-components/volume-control/VolumeControlComponent.tsx";
import { useIsMobile } from "@/hooks/use-mobile";
import { useElevenLabsConversation } from "@/hooks/useElevenLabsConversation";

interface ChatMessage {
    id: number;
    role: 'user' | 'ai' | 'loading';
    content: string;
    file?: any,
    preview: any;
}

const RequirementGatheringComponent = () => {
    const currentUser = CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_LOGGED_IN_USER_DATA);
    const formattedUser = currentUser ? JSON.parse(currentUser) : null;
    const userName = formattedUser?.name || "User";
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const greeting = commonService.generateGreeting();
    const chatHistoryRef = useRef<HTMLDivElement>(null);
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState<boolean>(false);
    const [currentFiles, setCurrentFiles] = useState<any>(null);
    const [inputValue, setInputValue] = useState<string>("");
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [hasSelectedMode, setHasSelectedMode] = useState<boolean>(false);
    
    // Get current prompt from Redux
    const { currentPrompt } = useSelector((state: IRootReducerState) => state.auth);
    const isCurrentPromptProcessed = useRef<boolean>(false);
    const hasReceivedFirstAIMessage = useRef<boolean>(false);

    // Debug mode - only in development
    const isDebugMode = import.meta.env.DEV;
    const isEnhanceMode = useRef<boolean>(false);

    const dispatch = useDispatch();

    const isNew = messages?.length === 0;
    
    // Forward declaration of handleStartAppGeneration to avoid circular dependency
    const handleStartAppGenerationRef = useRef<any>(null);
    const handleSendMessageRef = useRef<any>(null);
    
    // Handle voice message callback
    const handleVoiceTextMessage = useCallback((messageData: any) => {
        if (messageData && messageData.message && messageData.message.trim()) {
            // Check for requirements completion
            if (messageData.message.includes('REQUIREMENTS_COMPLETED: True') ||
                messageData.message.includes('=== FINAL PROMPT FOR DEVELOPMENT AGENT ===')) {
                // Show completion popup for 2-3 seconds
                commonService._alert.showToast("🎉 Requirements gathering completed! Starting app generation...", "success");

                // Small delay to let user see the message before proceeding
                setTimeout(() => {
                    if (handleStartAppGenerationRef.current) {
                        handleStartAppGenerationRef.current();
                    } else {
                        console.error('No session available for app generation');
                        commonService._alert.showToast("Voice session not available. Please try again.", "error");
                    }
                }, 1000); // 1 second delay
            }
            // Handle user messages from voice
            else if (messageData.source === 'user') {
                const userMessage: ChatMessage = {
                    id: Date.now(),
                    role: 'user',
                    content: messageData.message,
                    file: null,
                    preview: null,
                };

                setMessages((prevMessages) => [...prevMessages, userMessage]);
                
                // Add loading message to show AI is processing
                const loadingMessage: ChatMessage = {
                    id: Date.now() + 1,
                    role: 'loading',
                    content: '',
                    preview: null
                };
                setMessages((prevMessages) => [...prevMessages, loadingMessage]);
                setLoading(true);
            }
            // Handle AI messages
            else if (messageData.source === 'ai') {
                const aiMessage: ChatMessage = {
                    id: Date.now(),
                    role: 'ai',
                    content: messageData.message,
                    file: null,
                    preview: null,
                };

                // Remove any loading message and add the AI message
                setMessages((prevMessages) => {
                    const filteredMessages = prevMessages.filter(msg => msg.role !== 'loading');
                    const newMessages = [...filteredMessages, aiMessage];

                    return newMessages;
                });
                // Stop loading state
                setLoading(false);
                
                // Check if this is the first AI message and we have a current prompt to send
                if (!hasReceivedFirstAIMessage.current) {
                    hasReceivedFirstAIMessage.current = true;
                    
                    // If we have a current prompt from Redux and haven't processed it yet, send it automatically
                    if (currentPrompt && !isCurrentPromptProcessed.current) {
                        isCurrentPromptProcessed.current = true;
                        
                        // Send the prompt after a short delay to ensure AI is ready
                        setTimeout(() => {
                            if (handleSendMessageRef.current) {
                                handleSendMessageRef.current(currentPrompt);
                                // Clear the prompt from Redux
                                dispatch(setCurrentPrompt(""));
                            }
                        }, 1000);
                    }
                }
            }
        }
    }, [currentPrompt, dispatch]);
    
    // Use the ElevenLabs conversation hook
    const elevenLabsHook = useElevenLabsConversation({
        projectId,
        agentId: ENV.ELEVENLABS_REQUIREMENT_AGENT_ID,
        isRequirementGathering: true,
        onTextMessage: handleVoiceTextMessage,
        onVoiceStatusChange: () => {
            // Voice status is now handled by the hook
        },
        onAudioInputVolumeChange: () => {
            // Audio input volume is now handled by the hook
        }
    });

    // Commented out handleSendMessage - now using ElevenLabs agent for all messages
    // const handleSendMessage = useCallback(async (input: string) => {
    //     if (!input && !currentFiles) return;

    //     const userMessage: ChatMessage = {
    //         id: Date.now(),
    //         role: 'user',
    //         content: input,
    //         file: currentFiles?.length > 0 ?
    //             currentFiles[0]?.file
    //             : null,
    //         preview: currentFiles?.length > 0 ? currentFiles[0]?.preview : null,
    //     };


    //     setMessages((prevMessages) => [...prevMessages, userMessage]);

    //     const loadingMessage: ChatMessage = {id: Date.now(), role: 'loading', content: '', preview: null};


    //     setLoading(true);
    //     setMessages((prevMessages) => [...prevMessages, loadingMessage]);
    //     const payload = {
    //         projectId,
    //         input: input,
    //         file: currentFiles?.length > 0 ? currentFiles[0]?.file : null
    //     };
    //     const formData = commonService.getFormDataFromJSON(payload);
    //     setCurrentFiles(null);
    //     set
    // (false);

    //     try {
    //         commonService._agent_service.requirementGathering(formData)
    //             .then((response: any) => {
    //                 handleStatusChange()
    //                 updateMessages(response)
    //             })
    //     } catch (error: any) {
    //         console.error("API error:", error);
    //         // Check if it's a Cloudflare 524 timeout error
    //     }
    // }, [currentFiles, handleStatusChange,projectId,updateMessages]);

    const fileRenderer = useCallback((message: any) => {
        return (<>
            {
                message?.file &&
                <div className={'file-message'}>
                    <div
                        className={"p-3 bg-white border border-solid border-gray-300 rounded-xl dark:bg-neutral-800 dark:border-neutral-600 "}>
                        {
                            message?.preview ? (
                                <img
                                    src={message?.preview}
                                    alt="preview"
                                    className="size-4 rounded-lg"
                                />
                            ) : (
                                <span
                                    className="size-4 border border-gray-200 text-gray-500 rounded-lg dark:border-neutral-700 dark:text-neutral-500">
                                    {message?.file?.name?.split(".").pop().toUpperCase()}
                                </span>
                            )
                        }
                    </div>
                    <div className={'file-details'}>
                        <div className={'font-medium'}>
                            {message?.file?.name ? message?.file?.name : "File"}
                        </div>
                        <div className={'file-size'}>
                            {message?.file?.size}
                        </div>
                    </div>
                </div>
            }
            {
                message?.content &&
                <div className={'user-content'}>
                    {message?.content}
                </div>
            }

        </>)
    }, [])

    const renderMessages = useCallback((message: ChatMessage, index: number) => {
        if (message?.role === 'loading') {
            return <ChatLoaderComponent type={"requirement-step"} />;
        }

        return message?.role === "ai" && index + 1 === messages?.length ?
            <TextRenderAnimationComponent words={message.content} duration={0.2} /> :
            fileRenderer(message);
    }, [messages, fileRenderer]);

    useEffect(() => {
        if (chatHistoryRef.current) {
            chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
        }
    }, [messages]);

    const handleFileSelection = useCallback((files: any) => {
        setIsFileUploadModalOpen(false)
        setCurrentFiles(files)
    }, []);

    const handleRemoveFile = useCallback((index: number) => {
        setCurrentFiles((files: any[]) => files.filter((_, i) => i !== index));
    }, []);

    // Handle app generation from chat summary
    const handleStartAppGeneration = useCallback((promptFromSubject?: string) => {
        if (!projectId || messages.length <= 1 || !elevenLabsHook.isConnected) return;

        try {
            // Set PM conversation as successful to trigger prompt generation in chat screen
            dispatch(setPmConversationSuccessful(true));
            dispatch(setCurrentPmSessionId('requirement-session-' + projectId));
            dispatch(setConversationStep('requirement-gathering'));

            // If prompt is provided from subject, set it directly
            if (promptFromSubject) {
                dispatch(setConversationSummaryPrompt(promptFromSubject));
            }

            navigate(PROJECT_REACT_ROUTE + '/' + projectId);
            commonService._alert.showToast("App generation started with your requirements!", "success");
        } catch (error: any) {
            console.error("Error generating app prompt:", error);
            commonService._alert.showToast("Error generating app prompt. Please try again.", "error");
        }
    }, [projectId, messages, navigate, elevenLabsHook.isConnected, dispatch]);
    
    // Update ref whenever function changes
    useEffect(() => {
        handleStartAppGenerationRef.current = handleStartAppGeneration;
    }, [handleStartAppGeneration]);



    const handleSendMessage = useCallback((message: string) => {
        // Prevent sending messages if not connected
        if (!elevenLabsHook.isConnected) {
            CommonService._alert.showToast("Please connect to voice chat to continue.", "info");
            return;
        }
        
        // Add user message to local state immediately for better UX
        const userMessage: ChatMessage = {
            id: Date.now(),
            role: 'user',
            content: message,
            file: null,
            preview: null,
        };
        setMessages((prevMessages) => [...prevMessages, userMessage]);

        // Add loading message to show AI is processing
        const loadingMessage: ChatMessage = {
            id: Date.now() + 1,
            role: 'loading',
            content: '',
            preview: null
        };
        setMessages((prevMessages) => [...prevMessages, loadingMessage]);
        
        // Set loading state
        setLoading(true);
        commonService.handleMixpanelTrack("requirement_gathering_send_message",{
            project_id: projectId,
            message: message,
            is_requirement_gathering: true,
            timestamp: new Date().toISOString()
        });
        // Use the hook's sendTextMessage method
        elevenLabsHook.sendTextMessage(message);
        setInputValue('');
    }, [elevenLabsHook,projectId]);
    
    // Update ref whenever handleSendMessage changes
    useEffect(() => {
        handleSendMessageRef.current = handleSendMessage;
    }, [handleSendMessage]);

    // Cleanup function to disconnect ElevenLabs conversation
    const disconnectConversation = useCallback(() => {
        elevenLabsHook.stopConversation();
    }, [elevenLabsHook]);

    // Handle mode selection and start conversation
    const handleModeSelection = useCallback(async (mode: 'text' | 'voice') => {
        CommonService.handleMixpanelTrack(`Requirement Gathering ${mode} mode Selected`, {
            project_id: projectId,
            mode: mode,
            is_requirement_gathering: true,
            timestamp: new Date().toISOString()
        });
        setHasSelectedMode(true);
        
        // Call the appropriate start method directly
        if (mode === 'voice') {
            await elevenLabsHook.startVoiceConversation();
            commonService._alert.showToast("Voice conversation started", "success");
        } else {
            await elevenLabsHook.startTextConversation();
            commonService._alert.showToast("Text conversation started", "success");
        }
    }, [elevenLabsHook,projectId]);

    // Don't auto-start conversation - wait for user selection
    // useEffect removed - user must choose mode first

    // Subscribe to requirements gathering completion subject
    useEffect(() => {
        const subscription = commonService._communications.requirementsGatheringCompletedSubject.subscribe((messageData: any) => {
            // Show completion popup for 2-3 seconds
            commonService._alert.showToast("🎉 Requirements gathering completed! Starting app generation...", "success");

            commonService.handleMixpanelTrack("Requirements gathering completed", {
                project_id: projectId,
                is_requirement_gathering: true,
                timestamp: new Date().toISOString()
            });
            // Extract prompt from message data 
            let extractedPrompt = '';
            if (messageData && messageData.message) {
                const message = messageData.message;
                // Look for the final prompt section
                const promptMatch = message.match(/=== FINAL PROMPT FOR DEVELOPMENT AGENT ===\s*([\s\S]*)/);
                if (promptMatch && promptMatch[1]) {
                    extractedPrompt = promptMatch[1].trim();
                }
            }
            if (elevenLabsHook.isConnected) {
                    // If enhance mode is enabled, send the extracted prompt
                    const promptToSend = isEnhanceMode.current ? undefined  : extractedPrompt;
                    handleStartAppGeneration(promptToSend);
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [handleStartAppGeneration, elevenLabsHook.isConnected, isEnhanceMode,projectId]);

    // Handle starting new conversation - show mode selection screen
    const handleStartNewConversation = useCallback(async () => {
        
        // Reset all local component state to initial
        setMessages([]);
        setInputValue('');
        setLoading(false);
        setCurrentFiles(null);
        setImageFile(null);
        setIsFileUploadModalOpen(false);
        
        // Disconnect current conversation (this will reset hook state)
        await elevenLabsHook.stopConversation();
        
        // Show mode selection screen again
        setHasSelectedMode(false);
                commonService._alert.showToast("Conversation reset. Please select mode to start again.", "info");
    }, [elevenLabsHook]);

    return (
        <>
            <div className={`flex-1 flex flex-col items-center requirement-gathering-container ${isMobile ? 'requirement-gathering-mobile' : ''}`}>
                {/* Top Action Buttons - Skip and Restart */}
                <div className="w-full max-w-2xl flex justify-between gap-2">
                    {/* Restart button - show when conversation has started */}
                    {hasSelectedMode && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleStartNewConversation}
                                        className="text-sm gap-2"
                                        mixpanelEvent={{
                                            name: "Restart Conversation",
                                            properties: {
                                                project_id: projectId,
                                                is_requirement_gathering: true
                                            }
                                        }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                                            <path d="M21 3v5h-5"/>
                                            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                                            <path d="M3 21v-5h5"/>
                                        </svg>
                                        <span className="hidden sm:inline">Restart</span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Restart conversation and select mode again</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    
                    {/* Spacer when restart button is not visible */}
                    {!hasSelectedMode && <div />}
                    
                    {/* Skip button */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            disconnectConversation();
                            navigate(PROJECT_REACT_ROUTE + '/' + projectId + '?summary=skipped');
                            commonService._alert.showToast("Skipped project scoping with Zoya", "info");
                        }}
                        className="text-sm text-muted-foreground hover:text-foreground"
                        mixpanelEvent={{
                            name: "Skip Project Scoping",
                            properties: {
                                project_id: projectId,
                                is_requirement_gathering: true
                            }
                        }}
                    >
                        Skip project scoping
                    </Button>
                </div>
                {
                    isNew && !hasSelectedMode && (
                        <div className={'welcome-container'}>
                            <RequirementGatheringWelcomeComponent
                                greeting={greeting}
                                userName={userName}
                            />
                            {/* Mode Selection Component */}
                            <ModeSelectionComponent onModeSelection={handleModeSelection} />
                        </div>
                    )
                }
                <div className={"max-w-2xl w-full " + ((!isNew || hasSelectedMode) && " requirement-chat-container")}>
                            <div className={'chat-history'} ref={chatHistoryRef}>
                                {
                                    messages?.map((message, index) => {
                                        return (
                                            <div className={`message ${message.role}`} key={message.id+message.role}>
                                                {
                                                    (message.role === 'ai' || message.role === 'loading') ?
                                                        <JoyAvatar size={'chat'} /> :
                                                        <UserAvatarComponent />
                                                }
                                                <motion.div className="content"
                                                    initial={{ y: 100 }}
                                                    animate={{ y: 0 }}
                                                    style={{ whiteSpace: 'pre-wrap' }}
                                                >
                                                    {renderMessages(message, index) as ReactNode}
                                                </motion.div>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                            {/* {fileUploadMode && <span className={'pt-4'}>
                            <div
                                className={'w-full gradient-button'}>
                                <div className={'flex gap-3'}>
                                {
                                    currentFiles?.map((file: any, index: number) => {
                                            return (<div
                                                key={index + file?.file?.name}
                                                className="p-3 bg-white border border-solid border-gray-300 rounded-xl dark:bg-neutral-800 dark:border-neutral-600"
                                            >
                                                <div
                                                    className="flex items-center">
                                                    {
                                                        file.preview ? (
                                                            <img
                                                                src={file.preview}
                                                                alt="preview"
                                                                className="size-4 rounded-lg"
                                                            />
                                                        ) : (
                                                            <span
                                                                className="size-4 border border-gray-200 text-gray-500 rounded-lg dark:border-neutral-700 dark:text-neutral-500">
                                                                 {file.file.name.split(".").pop().toUpperCase()}
                                                                </span>
                                                        )
                                                    }

                                                </div>
                                            </div>)
                                        }
                                    )
                                }
                                    </div>
                                {
                                    currentFiles?.length >= 1 ?
                                        <div className={'d-flex align-items-end justify-between gap-2 mt-4'}>
                                            <div onClick={handleFileUploadModalOpen}>
                                                <div className={'flex align-items-center gap-3'}>
                                                    <UploadIcon size={16} strokeWidth={2} className={"text-primary"}/>
                                                    <div> Add more files</div>
                                                </div>
                                                <div className={'text-[var(--secondary-foreground)] text-sm mt-2'}>
                                                    Maximum of 5 files can be uploaded
                                                </div>
                                            </div>
                                            <Button
                                                onClick={() => {
                                                    // Send upload confirmation to ElevenLabs agent
                                                    commonService._communications.sendMessageSubject.next({ 
                                                        message: 'Confirm Upload', 
                                                        type: 'text' 
                                                    });
                                                }}
                                            >
                                                Confirm Upload
                                            </Button>
                                        </div> :
                                        <div className={'d-flex align-items-center gap-2'}
                                             onClick={handleFileUploadModalOpen}>
                                            <UploadIcon size={16} strokeWidth={2}/>
                                            Yes, Upload Files
                                        </div>
                                }
                            </div>
                            <div onClick={() => {
                                // Send message to ElevenLabs agent
                                commonService._communications.sendMessageSubject.next({ 
                                    message: "No, continue with a sample data", 
                                    type: 'text' 
                                });
                            }}
                                 className={'d-flex align-items-center gap-2 gradient-button gradient-button-cancel mt-4'}>
                                <SkipForward size={16} strokeWidth={2}/> No, continue with a sample data
                            </div>
                            </span>
                                        } */}
                    



                    <>
                        {/* Voice status indicators above input - only show in voice mode */}
                        {!elevenLabsHook.isTextOnlyMode && (elevenLabsHook.isSpeaking || elevenLabsHook.isListening) && (
                                <VoiceStatusIndicatorComponent
                                    isSpeaking={elevenLabsHook.isSpeaking}
                                    isListening={elevenLabsHook.isListening}
                                    className="voice-status-overlay"
                                    audioInputVolume={elevenLabsHook.audioInputVolume}
                                />
                        )}

                        {/* Unified Control Panel - Show when connected */}
                        {elevenLabsHook.isConnected && (
                            <div className="w-full max-w-2xl mb-4">
                                <div className="flex flex-wrap items-center justify-center gap-2">
                                    {/* Mode Indicator */}
                                    
                                    
                                    {/* Voice Controls - Show only in voice mode */}
                                    {!elevenLabsHook.isTextOnlyMode && (
                                        <VolumeControlComponent
                                            volume={elevenLabsHook.volume}
                                            onVolumeChange={elevenLabsHook.setVolume}
                                            projectId={projectId}
                                        />
                                    )}
                                    
                                    {/* Continue with App Generation Button - Show when there are multiple messages */}
                                    {messages.length > 1 && (
                                        <Button
                                            onClick={() => handleStartAppGeneration()}
                                            variant="default"
                                            size="sm"
                                            className="gap-2"
                                            mixpanelEvent={{
                                                name: "Continue with App Generation",
                                                properties: {
                                                    project_id: projectId,
                                                    is_requirement_gathering: true
                                                }
                                            }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 12h14"/>
                                                <path d="m12 5 7 7-7 7"/>
                                            </svg>
                                            <span className="hidden sm:inline">Continue with App Generation</span>
                                            <span className="sm:hidden">Continue</span>
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Debug Mode - Only show in development */}
                        {isDebugMode && (
                            <div className="w-full max-w-2xl mb-4 flex flex-col items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="enhance-mode"
                                        defaultChecked={isEnhanceMode.current}
                                        onChange={(e) => {
                                            isEnhanceMode.current = e.target.checked;
                                        }}
                                        className="rounded border-gray-300"
                                    />
                                    <label htmlFor="enhance-mode" className="text-sm text-gray-400">
                                        Continue with magic prompt
                                    </label>
                                </div>

                            </div>
                        )}
                        <div className={isMobile ? 'custom-input-mobile' : ''}>
                            {elevenLabsHook.isConnected &&
                                <CustomInputComponent
                                    onSend={(message: string) => {
                                        handleSendMessage(message);
                                    }}
                                    selectedFiles={currentFiles}
                                    projectId={projectId}
                                    loading={loading}
                                    isNew={messages?.length === 0}
                                    onFileRemove={(index: number) => handleRemoveFile(index)}
                                    value={inputValue}
                                    onInputChange={(value: string) => {
                                        setInputValue(value);
                                    }}
                                    imageFile={imageFile}
                                    onImageFileChange={setImageFile}
                                    
                                    isRequirementGathering={true}
                                    hideFileAttachment={true}
                                    hideEnhancement={true}
                                />
                            }
                        </div>
                    </>

                </div>


                <Dialog open={isFileUploadModalOpen} onOpenChange={() => setIsFileUploadModalOpen(false)}>
                    <DialogContent className={`ps-0 ${isMobile ? 'file-upload-dialog-mobile' : ''}`}>
                        <FileUploadComponent onUpload={handleFileSelection} isMultiple={false} />
                    </DialogContent>
                </Dialog>
                {elevenLabsHook.isConnecting && (
                    <div className={isMobile ? 'chat-initialization-mobile' : ''}>
                        <ChatInitializationLoaderComponent
                            isVisible={true}
                            message="Connecting to voice chat..."
                        />
                    </div>
                )}
            </div>


        </>
    );
};

export default RequirementGatheringComponent;
